import { useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { LeveragedBook } from '../components/LeveragedBook';
import { AddContract } from '../components/AddContract';
import { AccountOutflow, MoveMoneyButton } from '../components/AccountOutflow';
import { TabBar } from '../components/TabBar';
import { money, moneySigned, pctSigned, price as fmtPrice, shortDate } from '../lib/format';
import { useDisplay } from '../state/display';
import { useStore } from '../state/store';
import type { Nav } from '../App';
import { ActivityRow } from '../components/ActivityRow';
import { activityFeed, dayLabel, groupByDay, moneyThrough } from '../state/activity';
import { accountName } from '../lib/accounts';
import { canReachBrokers } from '../lib/brokerLink';
import { mexcStatus, type MexcStatus } from '../state/mexcStatus';
import { SPOT_ACCOUNT } from '../state/mexcSpot';
import { pct as spotPct, shares as spotShares } from '../lib/format';

/** The words beside "Open contracts": what is happening with MEXC, never blank. */
function mexcLabel(status: MexcStatus, syncedAt: string | undefined): string {
  switch (status) {
    case 'no-key': return 'no MEXC key on this device';
    case 'off': return 'MEXC reading off';
    case 'no-account': return 'no account for MEXC';
    case 'browser': return 'MEXC read in the app only';
    case 'failing': return 'MEXC read failing';
    case 'waiting': return 'MEXC key set, not read yet';
    case 'reading': return `read from MEXC ${(syncedAt && readAge(syncedAt)) || 'just now'}`;
  }
}
import { ageLabel as readAge, dateTime as readTime } from '../lib/format';

/* The leveraged book on its own. It behaves nothing like the share positions —
   a few percent against it matters far more — so it gets its own screen rather
   than sitting under eleven holdings. */
export function LeverageScreen({ nav }: { nav: Nav }) {
  const { book, state } = useStore();
  /* What the last account read found open here and absent there. Written by the
     sync, never acted on by it — see `mexcGone` in db.ts. */
  const goneFromExchange = new Set(state.settings.mexcGone ?? []);
  /* The shareholding beside each contract, for the companies that have one. The
     card is where a leveraged bet is looked at, and until now it was the only
     figure on the screen that did not know the book holds the shares as well. */
  const alsoHeld = new Map(book.positions
    .filter((p) => p.contractCount > 0)
    .map((p) => [p.ticker.toUpperCase(), {
      weight: p.weight, exposureWeight: p.exposureWeight, contractCount: p.contractCount,
    }]));
  const { currency, toDisplay } = useDisplay();
  const [note, setNote] = useState<string | null>(null);
  const [showMoney, setShowMoney] = useState(false);

  // Closed contracts leave the live book but stay on the record.
  const closed = state.data.perps
    .filter((p) => p.closedAt)
    .sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''));

  const { perpMarginUsd, perpUnrealisedUsd, totalUsd, futuresFreeUsd, futuresWalletUsd } = book.totals;
  const onMargin = perpMarginUsd > 0 ? (perpUnrealisedUsd / perpMarginUsd) * 100 : 0;
  /* The whole wallet against the book, not just what is posted. What sits
     unposted on the exchange is money on the exchange. */
  const shareOfBook = totalUsd > 0 ? (futuresWalletUsd / totalUsd) * 100 : 0;

  /* Realised on this venue, across every contract ever closed. The open
     positions say what is standing; this says what the trading took. */
  const futuresAccounts = state.data.accounts.filter((a) => a.isFutures);

  const realisedUsdt = state.data.perps.reduce(
    (sum, p) => sum + (p.closedAt ? (p.closedResultUsdt ?? 0) : (p.realizedUsdt ?? 0)),
    0,
  );

  /* The venue's own money, in and out: deposits, withdrawals and transfers to or
     from the futures account. Recorded all along but only reachable from the
     History screen, so checking what went to and came back from the exchange
     meant searching for it. The last few, here; the rest one tap away. */
  const futuresIds = futuresAccounts.map((a) => a.id).join(',');
  const { trades, dividends, cashflows, conversions, transfers, perps, holdings } = state.data;
  const moneyMoves = useMemo(
    () => moneyThrough(
      activityFeed({ trades, dividends, cashflows, conversions, transfers, perps, holdings }),
      new Set(futuresIds.split(',').filter(Boolean)),
    ),
    [trades, dividends, cashflows, conversions, transfers, perps, holdings, futuresIds],
  );
  const moneyDays = groupByDay(moneyMoves.slice(0, 8));
  const venueName = futuresAccounts[0]?.name ?? 'MEXC';
  const mexc = mexcStatus(state.settings, state.data.accounts, canReachBrokers());
  /* The spot wallet as the last read left it. Its coins are holdings on the spot
     account and its stablecoins that account's cash, so they are already in
     every total; this is where they are seen together, with the orders. */
  const spotRead = Boolean(state.settings.mexcSpotSyncedAt || state.settings.mexcSpotError);
  const spotCash = state.data.cash.find((c) => c.account === SPOT_ACCOUNT)?.amount ?? 0;
  const spotCoins = state.data.holdings.filter((h) => h.account === SPOT_ACCOUNT && h.shares > 0);
  const spotValue = (ticker: string, qty: number): number | null => {
    const p = book.positions.find((x) => x.ticker === ticker);
    return p && p.shares > 0 && p.marketValue > 0 ? (p.marketValue * qty) / p.shares : null;
  };
  const spotOrders = state.settings.mexcSpotOrders ?? [];
  const spotLocked = state.settings.mexcSpotLocked ?? 0;
  const spotGone = state.settings.mexcSpotGone ?? [];

  return (
    <div className="screen" data-tabbed="true">
      <Header onOpenSettings={() => nav.go({ name: 'settings' })} />

      {/* The headline is the whole wallet, not the open positions. "Leveraged
          equity" was margin plus unrealised, which reads as everything on the
          venue and is not — whatever is not posted against a contract is still
          sitting there, and is the thing the next position gets sized out of. */}
      <div className="card hero">
        <div className="hero-label">Leveraged equity</div>
        <div className="hero-total num">{money(toDisplay(futuresWalletUsd), currency)}</div>
        <div className="hero-change">
          <span className={`num ${perpUnrealisedUsd >= 0 ? 'up' : 'down'}`}>
            {perpUnrealisedUsd >= 0 ? '▲' : '▼'} {moneySigned(toDisplay(perpUnrealisedUsd), currency)}
            {'  '}{pctSigned(onMargin)} on margin
          </span>
          <span className="dim" style={{ fontSize: 12 }}>
            {shareOfBook.toFixed(1)}% of the book
          </span>
        </div>

        <div className="divider" />

        {/* The three things that add up to the figure above, and nothing else.
            Realised used to sit in this strip beside them, which made it read
            as a fourth component of a sum it is not part of — it is history,
            already inside the free balance, and the three-across layout was
            claiming otherwise. */}
        <div className="perp-strip" style={{ padding: 0 }}>
          <div>
            <div className="k">Margin</div>
            <div className="v num">{money(toDisplay(perpMarginUsd), currency)}</div>
          </div>
          <div>
            <div className="k">Free</div>
            <div className="v num">{money(toDisplay(futuresFreeUsd), currency)}</div>
          </div>
          <div>
            <div className="k">Profit</div>
            <div className={`v num ${perpUnrealisedUsd >= 0 ? 'up' : 'down'}`}>
              {moneySigned(toDisplay(perpUnrealisedUsd), currency)}
            </div>
          </div>
        </div>

        <div className="dim num" style={{ fontSize: 11.5, marginTop: 8, textAlign: 'center' }}>
          {money(toDisplay(perpMarginUsd), currency)} + {money(toDisplay(futuresFreeUsd), currency)}
          {' '}{perpUnrealisedUsd >= 0 ? '+' : '−'}{' '}
          {money(Math.abs(toDisplay(perpUnrealisedUsd)), currency)} ={' '}
          <strong style={{ color: 'var(--text)' }}>{money(toDisplay(futuresWalletUsd), currency)}</strong>
        </div>

        <div className="divider" />

        <div className="kv">
          <span className="k">Realised on closed contracts</span>
          <span className={`v num ${realisedUsdt >= 0 ? 'up' : 'down'}`}>
            {moneySigned(toDisplay(realisedUsdt), currency)}
          </span>
        </div>
        <div className="dim" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.45 }}>
          History, not a fourth part of the figure above. What closed contracts came to is already
          sitting in the free balance. It moves only when a contract is closed.
        </div>

        {/* Where "free" comes from. It is not worked out from anything: it is
            the balance you typed in, which the app only ever moves by itself
            when a contract is closed and its margin comes back. When the figure
            is wrong, this is the sentence that says where to fix it. */}
        <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
          <strong>Free</strong> is the balance recorded against{' '}
          {futuresAccounts.length > 0
            ? futuresAccounts.map((a) => a.name).join(', ')
            : 'no account marked as a futures wallet'}. It is typed in rather than worked out.
          Closing a contract adds its margin and its result to it; nothing else changes it.
        </div>
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }}
                onClick={() => nav.go({ name: 'settings', group: 'money' })}>
          Correct the balance ›
        </button>
      </div>

      {/* Money the exchange no longer has, and the record only you can give.
          Above the contracts because it is about the wallet, not a position. */}
      <AccountOutflow say={setNote} />

      {/* And a way in when the app never saw the fall — a withdrawal made
          before this existed, or from a device that was not syncing. */}
      <div style={{ marginTop: 10 }}>
        <MoveMoneyButton say={setNote}
          {...(futuresAccounts[0] ? { from: futuresAccounts[0].id } : {})} />
      </div>

      <section>
        <div className="section-head">
          <h2>Open contracts</h2>
          {/* Where these figures came from, and whether that is still working. It
              said "entered by hand" whatever was happening, so a read from MEXC
              that had been failing for days looked exactly like one that was fine. */}
          <span className="meta">{mexcLabel(mexc, state.settings.mexcSyncedAt)}</span>
        </div>
        {/* Whenever MEXC is not being read, the reason and the way to fix it, right
            under the heading. Every one of these used to read as "entered by
            hand", so a missing key and an exchange that was down looked alike. */}
        {mexc !== 'reading' ? (
          <div className="note" data-tone={mexc === 'failing' ? 'warn' : undefined} style={{ marginBottom: 10 }}>
            {mexc === 'no-key' ? (
              <>Nothing is read from MEXC on this device, so the contracts below are as entered.
                Settings do not travel between devices: the key has to be pasted in on each one.
                A read-only key is enough.</>
            ) : mexc === 'off' ? (
              <>The MEXC key is saved on this device, but automatic reading is switched off.</>
            ) : mexc === 'no-account' ? (
              <>The MEXC key is saved, but no account in the book is chosen to hold what it reads.
                Pick the futures account under the MEXC settings.</>
            ) : mexc === 'browser' ? (
              <>A browser tab cannot reach MEXC. The installed app on the phone or the Mac reads it.</>
            ) : mexc === 'waiting' ? (
              <>The MEXC key is set. The first read runs within a few minutes while the app is open
                on screen.</>
            ) : (
              <>The last read from MEXC failed: {state.settings.mexcError}.{' '}
                {state.settings.mexcSyncedAt
                  ? `The last one that worked was ${readTime(state.settings.mexcSyncedAt)}.`
                  : 'None has worked on this device yet.'}{' '}
                MEXC retires a key made without an IP address after 90 days; a new one is made on
                MEXC and pasted in on each device.</>
            )}
            {mexc !== 'browser' ? (
              <button className="btn btn-ghost btn-sm" style={{ display: 'block', marginTop: 6, paddingLeft: 0 }}
                      onClick={() => nav.go({ name: 'settings', group: 'brokers' })}>
                Open MEXC settings ›
              </button>
            ) : null}
          </div>
        ) : null}
        <LeveragedBook perps={book.perps} toDisplay={toDisplay} currency={currency} say={setNote}
                       onOpen={(id) => nav.go({ name: 'contract', id })}
                       goneFromExchange={goneFromExchange}
                       alsoHeld={alsoHeld} />
        <AddContract say={setNote} />

        {closed.length > 0 ? (
          <section>
            <div className="section-head">
              <h2>Closed</h2>
              <span className="meta">{closed.length} · tap for the figures</span>
            </div>
            <div className="rows">
              {closed.map((p) => {
                const result = p.closedResultUsdt ?? 0;
                /* Return on what was actually posted against it, which is the
                   figure that says whether the trade was any good — +18 on 170
                   of margin and +18 on 17,000 are not the same trade. Blank on
                   contracts closed by the version that wiped the margin. */
                const onMargin = p.marginUsdt > 0 ? (result / p.marginUsdt) * 100 : null;
                const held = p.openedAt && p.closedAt
                  ? Math.max(0, Math.round(
                      (new Date(p.closedAt).getTime() - new Date(p.openedAt).getTime()) / 86_400_000))
                  : null;
                return (
                  <button className="row" key={p.id} style={{ marginBottom: 8 }}
                          aria-label={`Open the ${p.display} contract`}
                          onClick={() => nav.go({ name: 'contract', id: p.id })}>
                    <div className="row-top">
                      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{p.display}</span>
                      <span className="perp-side" data-side={p.side}>
                        {p.side.toUpperCase()} {p.leverage}x
                      </span>
                      <span className="row-right">
                        <span className={`num ${result >= 0 ? 'up' : 'down'}`} style={{ fontWeight: 600 }}>
                          {moneySigned(toDisplay(result), currency)}
                        </span>
                        {onMargin === null ? null : (
                          <span className={`num ${onMargin >= 0 ? 'up' : 'down'}`}
                                style={{ display: 'block', fontSize: 11.5, textAlign: 'right' }}>
                            {pctSigned(onMargin)} on margin
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="row-meta">
                      <span>closed {shortDate(p.closedAt!)}</span>
                      {held === null ? null : <span>held {held}d</span>}
                      {p.marginUsdt > 0 ? (
                        <span className="num">{money(toDisplay(p.marginUsdt), currency)} margin</span>
                      ) : null}
                      {p.entryPrice > 0 ? (
                        <span className="num">in at {fmtPrice(p.entryPrice)}</span>
                      ) : null}
                      {p.closes && p.closes.length > 1 ? (
                        <span>{p.closes.length} closes</span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}
        {note ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>{note}</div>
        ) : null}
      </section>

      {/* The spot wallet, beside the futures one, once a read has tried it. */}
      {spotRead ? (
        <section>
          <div className="section-head">
            <h2>Spot</h2>
            <span className="meta">
              {state.settings.mexcSpotError
                ? 'spot read failing'
                : state.settings.mexcSpotSyncedAt
                  ? `read ${readAge(state.settings.mexcSpotSyncedAt) ?? 'just now'}`
                  : 'not read yet'}
            </span>
          </div>
          <div className="card card-pad">
            {state.settings.mexcSpotError ? (
              <div className="note" data-tone="warn" style={{ marginBottom: 10 }}>
                The last spot read failed: {state.settings.mexcSpotError}. The key needs reading
                allowed for spot as well as futures.
              </div>
            ) : null}
            <div className="kv">
              <span className="k">USDT and other stablecoins</span>
              <span className="v num">{money(toDisplay(spotCash), currency)}</span>
            </div>
            {spotLocked > 0.005 ? (
              <div className="kv">
                <span className="k">of which in open orders</span>
                <span className="v num">{money(toDisplay(spotLocked), currency)}</span>
              </div>
            ) : null}
            {spotCoins.map((h) => {
              const value = spotValue(h.ticker, h.shares);
              return (
                <div className="kv" key={h.id}>
                  <span className="k">{spotShares(h.shares)} {h.ticker}</span>
                  <span className="v num">{value === null ? 'no price yet' : money(toDisplay(value), currency)}</span>
                </div>
              );
            })}

            {spotOrders.length > 0 ? (
              <>
                <div className="divider" />
                <div className="fin-title">Open orders</div>
                {spotOrders.map((o) => (
                  <div className="kv" key={`${o.symbol}-${o.at}-${o.price}`}>
                    <span className="k">
                      {o.side === 'buy' ? 'Buy' : 'Sell'} {spotShares(o.quantity)} {o.symbol.replace(/USDT$/, '')} at {fmtPrice(o.price)}
                    </span>
                    <span className="v num dim">
                      {o.quantity > 0 ? spotPct((o.filled / o.quantity) * 100, 0) : '—'} filled
                    </span>
                  </div>
                ))}
              </>
            ) : null}

            {spotGone.length > 0 ? (
              <div className="note" style={{ marginTop: 10 }}>
                {spotGone.join(', ')} {spotGone.length === 1 ? 'is' : 'are'} still in the book on the
                spot account but no longer in the wallet. Nothing is removed on its own: record the
                sale with its price on the holding.
              </div>
            ) : null}

            <div className="hint" style={{ marginTop: 10 }}>
              Counted in the book: the coins as holdings on the MEXC spot account, priced like any
              other coin, and the stablecoins as its cash. MEXC does not report what a coin cost, so
              the cost reads as unknown until it is entered on the holding. Orders are listed for the
              coins held; a buy order for a coin not held shows only as money in open orders.
            </div>
          </div>
        </section>
      ) : null}

      {/* Money in and out, at the foot and folded. Above the contracts it pushed
          them down a screen, and a list of deposits is something checked now and
          then, not read every time the screen opens. */}
      <section>
        <div className="section-head">
          <h2>Money in and out</h2>
          <button className="btn btn-ghost btn-sm" aria-expanded={showMoney}
                  onClick={() => setShowMoney((v) => !v)}>
            {showMoney ? 'Hide' : moneyMoves.length ? `Show ${moneyMoves.length}` : 'Show'}
          </button>
        </div>
        {showMoney ? (
          moneyDays.length === 0 ? (
            <div className="card card-pad">
              <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                Nothing recorded as paid into, taken out of or moved from {venueName} yet. Deposits go
                in under Settings, Money in and out; a transfer or withdrawal is recorded with the
                button near the top of this screen.
              </p>
            </div>
          ) : (
            <>
              {moneyDays.map((group, i) => (
                <div key={group.day}>
                  <div className="activity-day" style={i === 0 ? { marginTop: 0 } : undefined}>
                    {dayLabel(group.day)}
                  </div>
                  <div className="card activity-list">
                    {group.items.map((item) => (
                      <ActivityRow key={item.id} item={item} open={false}
                                   nameOf={(id) => accountName(state.data.accounts, id)}
                                   onOpen={() => nav.go({ name: 'settings', group: 'money' })} />
                    ))}
                  </div>
                </div>
              ))}
              <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 8 }}
                      onClick={() => nav.go({ name: 'activity', filter: 'cash', query: venueName })}>
                See all in History ›
              </button>
            </>
          )
        ) : null}
      </section>

      <p className="dim" style={{ fontSize: 11, textAlign: 'center', marginTop: 24, lineHeight: 1.5 }}>
        Marks come from the exchange. Size, entry, margin and liquidation are yours to enter. The
        app holds no exchange keys and places no orders.
      </p>

      <TabBar current="leverage" go={nav.go} />
    </div>
  );
}
